import { db, cardStateKey, type CardState } from "./db";
import { directionsFor, isMastered } from "./study";
import type { InstalledPack } from "./packs";

export type PackProgress = { mastered: number; inProgress: number; new: number; total: number };

const EMPTY_PROGRESS: PackProgress = { mastered: 0, inProgress: 0, new: 0, total: 0 };

/**
 * Per-pack learning progress, at concept granularity (not per-direction
 * card, unlike dueCounts.ts) — this is what "X of Y mastered" on a pack
 * means to a user, regardless of how many drill directions it expands to.
 *
 * A concept is:
 *  - `new`: no cardState row in any of the pack's directions yet.
 *  - `mastered`: a cardState row exists for every direction the pack was
 *    installed with, and all of them pass {@link isMastered}.
 *  - `inProgress`: everything else (partially graded, or graded but not
 *    yet past the mastered threshold in every direction).
 */
export async function computePackProgress(pack: InstalledPack): Promise<PackProgress> {
  const concepts = await db.concepts.where("packId").equals(pack.id).toArray();
  const total = concepts.length;
  if (total === 0) return EMPTY_PROGRESS;

  const dirs = directionsFor(pack.langs);
  if (dirs.length === 0) return { ...EMPTY_PROGRESS, new: total, total };

  const keysByConcept = new Map<string, string[]>();
  const allKeys: string[] = [];
  for (const c of concepts) {
    const keys = dirs.map((d) => cardStateKey(c.id, d));
    keysByConcept.set(c.id, keys);
    allKeys.push(...keys);
  }

  const states = await db.cardStates.where("key").anyOf(allKeys).toArray();
  const stateByKey = new Map(states.map((s) => [s.key, s]));

  let mastered = 0;
  let inProgress = 0;
  let newCount = 0;
  for (const c of concepts) {
    const keys = keysByConcept.get(c.id) ?? [];
    const conceptStates = keys
      .map((k) => stateByKey.get(k))
      .filter((s): s is CardState => !!s);
    if (conceptStates.length === 0) {
      newCount += 1;
    } else if (conceptStates.length === keys.length && conceptStates.every(isMastered)) {
      mastered += 1;
    } else {
      inProgress += 1;
    }
  }

  return { mastered, inProgress, new: newCount, total };
}

/** Batch version of {@link computePackProgress} for the Dashboard/Library
    lists, which render several installed packs at once. */
export async function computePacksProgress(
  packs: InstalledPack[],
): Promise<Map<string, PackProgress>> {
  const entries = await Promise.all(
    packs.map(async (p) => [p.id, await computePackProgress(p)] as const),
  );
  return new Map(entries);
}
